import { CloudOff, FileQuestion, KeyRound, RefreshCw, ServerCrash, ShieldAlert, Unplug } from "lucide-react";
import type { ReactNode } from "react";
import { isApiError, isContractError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface QueryLike {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
}

/**
 * Explains a failed request in operational terms: missing contract, forbidden, not found,
 * session ended, network failure, or API failure (with request id for support).
 */
export function ErrorPanel({
  error,
  onRetry,
  resource = "record",
  compact = false,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  resource?: string;
  compact?: boolean;
  className?: string;
}) {
  let Icon = ServerCrash;
  let title = "The API returned an error";
  let body: ReactNode = error instanceof Error ? error.message : "Unknown error";
  let retry = true;
  if (isContractError(error)) {
    Icon = Unplug;
    title = "Awaiting backend API contract";
    body = (
      <>
        <span className="block">{error.description}.</span>
        <span className="mt-1 block font-mono text-[11px]">TODO: BACKEND API CONTRACT REQUIRED · {error.operation}</span>
      </>
    );
    retry = false;
  } else if (isApiError(error)) {
    if (error.isForbidden) {
      Icon = ShieldAlert;
      title = "Not permitted";
      body = "Your admin role doesn't allow viewing this. The backend decides access.";
      retry = false;
    } else if (error.isNotFound) {
      Icon = FileQuestion;
      title = `${resource.charAt(0).toUpperCase()}${resource.slice(1)} not found`;
      body = "It may have been deleted, or the ID is wrong.";
      retry = false;
    } else if (error.isUnauthenticated) {
      Icon = KeyRound;
      title = "Session ended";
      body = "Sign in again to continue.";
      retry = false;
    } else if (error.isNetwork) {
      Icon = CloudOff;
      title = "Can't reach the API";
      body = "Network failure. Check your connection or VPN, then retry.";
    } else {
      body = (
        <>
          {error.userMessage}
          {error.requestId ? <span className="mt-1 block font-mono text-[11px]">request {error.requestId}</span> : null}
        </>
      );
    }
  }
  return (
    <div
      role="alert"
      className={cn("flex items-start gap-3 rounded-md border border-dashed bg-card", compact ? "p-3" : "p-5", className)}
      data-testid="error-panel"
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{title}</p>
        <div className="mt-0.5 text-muted-foreground text-xs">{body}</div>
      </div>
      {retry && onRetry ? (
        <Button size="xs" variant="outline" onClick={onRetry}>
          <RefreshCw data-icon="inline-start" />
          Retry
        </Button>
      ) : null}
    </div>
  );
}

/** Loading → error → content, for any query. */
export function QueryState<T>({
  query,
  data,
  children,
  skeleton,
  resource,
  className,
}: {
  query: QueryLike;
  data: T | undefined;
  children: (data: T) => ReactNode;
  skeleton?: ReactNode;
  resource?: string;
  className?: string;
}) {
  if (query.isError && data === undefined)
    return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} resource={resource} className={className} />;
  if (data === undefined) return <>{skeleton ?? <Skeleton className={cn("h-24", className)} />}</>;
  return <>{children(data)}</>;
}
